import {
  ANY_CANDIDATE_LOCATION,
  DateFormats,
  RequestPriorityBoost,
  RequestPriorityBoosts,
  RequestPriorityBoostTags,
  candidateDefaultEmploymentForms,
  candidateDefaultWorkFormats,
  candidateDefaultWorkloads,
  inStaffCandidateStatusesIds,
  requestDefaultLanguage,
} from '@constants';
import { CandidateWorkFormatsElement, SelectOption } from '@innowise-group/core';
import { calculateDateWithTimeZone, localizedNameObject } from '@innowise-group/utilities';
import { format, parseISO } from 'date-fns';

import { DictionaryValue } from '../common';
import {
  LocalizedRequestCandidateItemResponse,
  LocalizedRequestEmployee,
  LocalizedRequestItemResponse,
  RequestCandidateItemResponse,
  RequestEmployee,
  RequestItem,
  RequestItemResponse,
  RequestLanguagePayload,
  RequestPayload,
} from './requests.types';

const mapCheckedToDictionaryIds = (items: CandidateWorkFormatsElement[]): string[] =>
  items.reduce<string[]>((accum, item) => {
    if (item.checked) {
      accum.push(item.dictionaryValueId);
    }
    return accum;
  }, []);

const mapDictionaryIdsToChecked = (
  defaultItems: CandidateWorkFormatsElement[],
  selectedIds: string[] = [],
): CandidateWorkFormatsElement[] =>
  defaultItems.map((item) => ({
    dictionaryValueId: item.dictionaryValueId,
    checked: selectedIds.includes(item.dictionaryValueId),
  }));

// A dictionary value without a translation into the request language comes with `translation: null`
export const getDictionaryValueTitle = ({ valueId, translation }: DictionaryValue): string => translation || valueId;

// A country, a city or a unit missing from its directory comes with `name: null`, so the id stands in for the name
export const getNamedValueTitle = ({ id, name }: { id: number | string; name?: string | null }): string =>
  name || `#${id}`;

const mapDictionaryValueToSelectOption = (value: DictionaryValue): SelectOption => ({
  value: value.valueId,
  title: getDictionaryValueTitle(value),
});

const mapSalaryToFormValue = (salary?: number | null): string =>
  salary === null || salary === undefined ? '' : salary.toString();

const mapSalaryToPayload = (salary: string): number | null => {
  const value = (salary || '').trim();
  return value ? Number(value) : null;
};

// The deadline is a date without time: parsing it as local midnight keeps the same day in any time zone
export const parseRequestDate = (date?: string | null): Date | null => (date ? parseISO(date) : null);

export const formatRequestDate = (date?: string | null): string =>
  date ? format(parseISO(date), DateFormats.DayFirst) : '-';

// `createdDate` and `lastModifiedDate` are UTC without a zone: the day is taken in the user's time zone, like the time
export const formatRequestTimestampDate = (date?: string | null): string =>
  date ? format(calculateDateWithTimeZone(date), DateFormats.DayFirst) : '-';

// An employee missing from the directory comes without names, so the email or the id stands in for them
export const localizeRequestEmployee = (employee: RequestEmployee): LocalizedRequestEmployee => {
  const hasName = !!(employee.lastNameRu || employee.lastNameEn || employee.firstNameRu || employee.firstNameEn);

  return {
    ...employee,
    localizedFullName: hasName ? localizedNameObject(employee).localizedFullName : employee.email || `#${employee.id}`,
  };
};

export const getRequestPriorityTag = (priority: string, priorityBoost: RequestPriorityBoost = 0): string =>
  RequestPriorityBoostTags[priorityBoost] || priority;

// The opposite of the tag: a boosted tag keeps the base priority and sets its boost, a base one drops the boost
export const getRequestPriorityChange = (
  { priority }: Pick<RequestItemResponse, 'priority'>,
  priorityTag: string,
): Pick<RequestItemResponse, 'priority' | 'priorityBoost'> => {
  const boost = RequestPriorityBoosts.find((value) => RequestPriorityBoostTags[value] === priorityTag);

  return boost ? { priority, priorityBoost: boost } : { priority: priorityTag, priorityBoost: 0 };
};

// A candidate without a status or a photo comes without these fields
export const localizeRequestCandidate = ({
  candidate,
  attachedBy,
  decidedBy,
  ...item
}: RequestCandidateItemResponse): LocalizedRequestCandidateItemResponse => ({
  ...item,
  candidate: {
    ...candidate,
    responsibleEmployee: { ...candidate.responsibleEmployee, ...localizedNameObject(candidate.responsibleEmployee) },
    ...localizedNameObject(candidate),
  },
  attachedBy: localizeRequestEmployee(attachedBy),
  decidedBy: decidedBy ? localizeRequestEmployee(decidedBy) : null,
});

const localizeRequestCandidates = ({
  linkedCandidatesIds,
  linkedCandidatesItems,
}: RequestItemResponse): LocalizedRequestItemResponse['linkedCandidatesItems'] =>
  (linkedCandidatesIds || []).reduce<LocalizedRequestItemResponse['linkedCandidatesItems']>((accum, id) => {
    if (linkedCandidatesItems?.[id]) accum[id] = localizeRequestCandidate(linkedCandidatesItems[id]);
    return accum;
  }, {});

export const localizeRequest = (request: RequestItemResponse): LocalizedRequestItemResponse => ({
  ...request,
  customers: (request.customers || []).map(localizeRequestEmployee),
  executors: (request.executors || []).map(localizeRequestEmployee),
  linkedCandidatesItems: localizeRequestCandidates(request),
  priorityTag: getRequestPriorityTag(request.priority, request.priorityBoost),
});

// US-10.2: the found candidates are the attached ones who became employees, interns or are on probation
export const getRequestFoundCandidatesCount = ({
  linkedCandidatesIds,
  linkedCandidatesItems,
}: Pick<LocalizedRequestItemResponse, 'linkedCandidatesIds' | 'linkedCandidatesItems'>): number =>
  (linkedCandidatesIds || []).filter((id) =>
    inStaffCandidateStatusesIds.includes(linkedCandidatesItems?.[id]?.candidate.status?.status?.id),
  ).length;

export const mapRequestItemToPayload = (data: RequestItem): RequestPayload => {
  const candidateCountryId =
    data.candidateCountry && data.candidateCountry !== ANY_CANDIDATE_LOCATION ? Number(data.candidateCountry) : null;

  return {
    name: (data.name || '').trim(),
    customerIds: data.customers.map((item) => Number(item.value)),
    isBillable: data.isBillable === 'true',
    unitId: Number(data.unitId),
    divisionId: Number(data.divisionId),
    priority: data.priority,
    deadline: data.deadline ? format(new Date(data.deadline), DateFormats.YearFirst) : null,
    professionIds: data.professions.map((item) => item.value),
    domainIds: data.domains.map((item) => item.value),
    requiredCount: Number(data.requiredCount),
    forProject: !!data.forProject,
    forPreoffer: !!data.forPreoffer,
    countryIds: data.countries.map((item) => Number(item.value)),
    cityIds: data.cities.map((item) => item.value),
    officeIds: data.offices.map((item) => item.value),
    candidateCountryId,
    candidateCityId: candidateCountryId && data.candidateCity ? data.candidateCity : null,
    languages: data.languages.reduce<RequestLanguagePayload[]>((accum, item) => {
      if (item.language) {
        accum.push({ language: item.language, ...(item.level && { level: item.level }) });
      }
      return accum;
    }, []),
    salaryMin: mapSalaryToPayload(data.salaryMin),
    salaryMax: mapSalaryToPayload(data.salaryMax),
    currency: data.currency,
    workloads: mapCheckedToDictionaryIds(data.workloads),
    workFormats: mapCheckedToDictionaryIds(data.workFormats),
    employmentForms: mapCheckedToDictionaryIds(data.employmentForms),
    comment: (data.comment || '').trim(),
    isCommentRestricted: !!data.isCommentRestricted,
  };
};

export const mapRequestResponseToRequestItem = (request: LocalizedRequestItemResponse): RequestItem => ({
  name: request.name || '',
  customers: (request.customers || []).map((item) => ({
    value: item.id.toString(),
    title: item.localizedFullName,
  })),
  isBillable: request.isBillable ? 'true' : 'false',
  unitId: request.unit?.id?.toString() || '',
  divisionId: request.division?.id?.toString() || '',
  priority: request.priority || '',
  deadline: parseRequestDate(request.deadline),
  professions: (request.professions || []).map(mapDictionaryValueToSelectOption),
  domains: (request.domains || []).map(mapDictionaryValueToSelectOption),
  requiredCount: request.requiredCount || 1,
  forProject: !!request.forProject,
  forPreoffer: !!request.forPreoffer,
  countries: (request.countries || []).map((item) => ({ title: item.name, value: item.id.toString() })),
  // A city missing from the directory comes with its id only: no name and no country
  cities: (request.cities || []).map((item) => ({
    title: item.name,
    value: item.id.toString(),
    parent: item.countryId?.toString(),
  })),
  offices: (request.offices || []).map((item) => ({
    title: getDictionaryValueTitle(item),
    value: item.valueId,
    parent: item.parentId,
  })),
  candidateCountry: request.candidateCountry?.id?.toString() || '',
  candidateCity: request.candidateCity?.id?.toString() || '',
  languages: request.languages?.length
    ? request.languages.map(({ language, level }) => ({ language, level: level || '' }))
    : [{ ...requestDefaultLanguage }],
  salaryMin: mapSalaryToFormValue(request.salaryMin),
  salaryMax: mapSalaryToFormValue(request.salaryMax),
  currency: request.currency,
  workloads: mapDictionaryIdsToChecked(candidateDefaultWorkloads, request.workloads),
  workFormats: mapDictionaryIdsToChecked(candidateDefaultWorkFormats, request.workFormats),
  employmentForms: mapDictionaryIdsToChecked(candidateDefaultEmploymentForms, request.employmentForms),
  comment: request.comment || '',
  isCommentRestricted: !!request.isCommentRestricted,
});
